import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { formatCNPJ, formatNome } from '@/utils/format'
import { formatCPF } from '@/components/form/input/cpf-mask-input'
import { friendlyNameMaps } from '@/components/form/input/StatusMapper'
import { produtosLabels } from '@/features/products/utils'
import { TypeServico } from '@/lib/enums'

type ReportContact = {
  id?: string
  type?: string
  nome?: string | null
  contato?: string | null
  email?: string | null
  document?: string | null
}

type ReportItem = {
  client?: any
  owner?: any
  products?: Array<{
    id?: string
    typeServico?: string | null
    typeRegime?: string | null
    status?: string | null
    nomeAnalista?: string | null
    createdDate?: string | Date | null
  }>
  contacts?: ReportContact[]
  responsavel?: { id?: string; name?: string; email?: string } | null
  gerente?: { id?: string; name?: string; email?: string } | null
}

type ReportPayload = {
  id_tenant: string
  generatedAt: string
  totalClients: number
  items: ReportItem[]
  tenantDisplayName?: string
}

export async function exportClientsTenantReport(report: ReportPayload) {
  const workbook = new ExcelJS.Workbook()
  const summarySheet = workbook.addWorksheet('Clientes')
  const contactsSheet = workbook.addWorksheet('Contatos')
  const productsSheet = workbook.addWorksheet('Produtos')

  buildSummarySheet(summarySheet, report)
  buildContactsSheet(contactsSheet, report)
  buildProductsSheet(productsSheet, report)

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })

  saveAs(blob, `relatorio-clientes-${report.id_tenant}-${new Date().toISOString().slice(0, 10)}.xlsx`)
}

function buildSummarySheet(worksheet: ExcelJS.Worksheet, report: ReportPayload) {
  const tenantDisplayName = resolveTenantDisplayName(report)

  worksheet.mergeCells('A1:S1')
  worksheet.getCell('A1').value = `Relatório de clientes por empresa: ${tenantDisplayName}`
  worksheet.getCell('A1').font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } }
  worksheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E79' } }

  worksheet.mergeCells('A2:S2')
  worksheet.getCell('A2').value = `Gerado em ${formatReportDate(report.generatedAt)} | Total de clientes: ${report.totalClients}`
  worksheet.getCell('A2').font = { italic: true }

  worksheet.addRow([
    'Razão Social',
    'CNPJ/CPF',
    'Status',
    'Cidade/UF',
    'Responsável',
    'Gerente',
    'Proprietário',
    'CPF Proprietário',
    'Proprietário Verificado',
    'Contato Proprietário',
    'E-mail Proprietário',
    'Produtos',
    'Status dos Produtos',
    'Nome Contador',
    'Contato Contador',
    'E-mail Contador',
    'Contatos Adicionais',
    'Criado em',
    'Procuração'
  ])

  styleHeaderRow(worksheet.getRow(3))

  for (const item of report.items ?? []) {
    const client = item.client ?? {}
    const contacts = Array.isArray(item.contacts) ? item.contacts : []
    const products = Array.isArray(item.products) ? item.products : []
    const extraContacts = contacts.filter(contact => !['OWNER', 'ACCOUNTANT'].includes(contact.type ?? ''))
    const formattedProducts = products.map(product => formatProductName(product.typeServico)).filter(Boolean)
    const formattedProductStatuses = products
      .map(product => {
        const productName = formatProductName(product.typeServico)
        const productStatus = formatProductStatus(product.status)
        return [productName, productStatus].filter(Boolean).join(' - ')
      })
      .filter(Boolean)

    worksheet.addRow([
      client.razao_social ?? '',
      formatCNPJ(client.cnpjcpf ?? ''),
      client.status ?? '',
      [client.municipio, client.estado].filter(Boolean).join('/') || '',
      item.responsavel?.name ?? client.nome_responsavel ?? '',
      item.gerente?.name ?? client.nome_gerente ?? '',
      formatNome(item.owner?.nomeProprietario ?? client.nomeProprietario ?? ''),
      formatCPF(item.owner?.cpfProprietario ?? ''),
      item.owner?.verifiedOwner ? 'Sim' : 'Não',
      client.contatoProprietario ?? '',
      client.emailProprietario ?? '',
      formattedProducts.join(' | '),
      formattedProductStatuses.join(' | '),
      client.nomeContador ?? '',
      client.contatoContador ?? '',
      client.emailContador ?? '',
      extraContacts
        .map(contact => [contact.type, contact.nome, contact.contato, contact.email].filter(Boolean).join(' | '))
        .join(' || '),
      formatReportDate(client.createdDate),
      client.procuracaoHabilitada ?? ''
    ])
  }

  worksheet.columns = [
    { width: 32 },
    { width: 20 },
    { width: 16 },
    { width: 18 },
    { width: 24 },
    { width: 24 },
    { width: 24 },
    { width: 18 },
    { width: 16 },
    { width: 18 },
    { width: 28 },
    { width: 36 },
    { width: 42 },
    { width: 24 },
    { width: 18 },
    { width: 28 },
    { width: 48 },
    { width: 14 },
    { width: 26 }
  ]

  applyGrid(worksheet)
  worksheet.views = [{ state: 'frozen', ySplit: 3 }]
}

function buildContactsSheet(worksheet: ExcelJS.Worksheet, report: ReportPayload) {
  worksheet.mergeCells('A1:H1')
  worksheet.getCell('A1').value = 'Contatos por cliente'
  worksheet.getCell('A1').font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } }
  worksheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4B4B4B' } }

  worksheet.addRow(['Cliente', 'CNPJ/CPF', 'Tipo', 'Nome', 'Documento', 'Contato', 'E-mail', 'Proprietário'])
  styleHeaderRow(worksheet.getRow(2))

  for (const item of report.items ?? []) {
    const client = item.client ?? {}
    const contacts = Array.isArray(item.contacts) ? item.contacts : []

    if (contacts.length === 0) {
      worksheet.addRow([
        client.razao_social ?? '',
        formatCNPJ(client.cnpjcpf ?? ''),
        'SEM_CONTATO',
        '',
        '',
        '',
        '',
        item.owner?.nomeProprietario ?? ''
      ])
      continue
    }

    for (const contact of contacts) {
      worksheet.addRow([
        client.razao_social ?? '',
        formatCNPJ(client.cnpjcpf ?? ''),
        contact.type ?? '',
        contact.nome ?? '',
        contact.document ?? '',
        contact.contato ?? '',
        contact.email ?? '',
        item.owner?.nomeProprietario ?? ''
      ])
    }
  }

  worksheet.columns = [
    { width: 30 },
    { width: 20 },
    { width: 16 },
    { width: 24 },
    { width: 18 },
    { width: 18 },
    { width: 28 },
    { width: 24 }
  ]

  applyGrid(worksheet)
  worksheet.views = [{ state: 'frozen', ySplit: 2 }]
}

function buildProductsSheet(worksheet: ExcelJS.Worksheet, report: ReportPayload) {
  worksheet.mergeCells('A1:G1')
  worksheet.getCell('A1').value = 'Produtos por cliente'
  worksheet.getCell('A1').font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } }
  worksheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF166534' } }

  worksheet.addRow(['Cliente', 'CNPJ/CPF', 'Produto', 'Status', 'Regime', 'Analista', 'Criado em'])
  styleHeaderRow(worksheet.getRow(2))

  for (const item of report.items ?? []) {
    const client = item.client ?? {}
    const products = Array.isArray(item.products) ? item.products : []

    if (products.length === 0) {
      worksheet.addRow([
        client.razao_social ?? '',
        formatCNPJ(client.cnpjcpf ?? ''),
        'SEM_PRODUTO',
        '',
        '',
        '',
        ''
      ])
      continue
    }

    for (const product of products) {
      worksheet.addRow([
        client.razao_social ?? '',
        formatCNPJ(client.cnpjcpf ?? ''),
        formatProductName(product.typeServico),
        formatProductStatus(product.status),
        product.typeRegime ?? '',
        product.nomeAnalista ?? '',
        formatReportDate(product.createdDate)
      ])
    }
  }

  worksheet.columns = [
    { width: 30 },
    { width: 20 },
    { width: 32 },
    { width: 24 },
    { width: 24 },
    { width: 24 },
    { width: 14 }
  ]

  applyGrid(worksheet)
  worksheet.views = [{ state: 'frozen', ySplit: 2 }]
}

function styleHeaderRow(row: ExcelJS.Row) {
  row.font = { bold: true, color: { argb: 'FFFFFFFF' } }
  row.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2563EB' } }
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
  })
}

function applyGrid(worksheet: ExcelJS.Worksheet) {
  const thinBorder = {
    top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
    left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
    bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
    right: { style: 'thin', color: { argb: 'FFD1D5DB' } }
  } as const

  worksheet.eachRow((row, rowNumber) => {
    row.eachCell(cell => {
      cell.border = thinBorder
      if (rowNumber > 2) {
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true }
      }
    })
  })
}

function formatReportDate(value?: string | Date | null) {
  if (!value) return ''

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return new Intl.DateTimeFormat('pt-BR').format(date)
}

function resolveTenantDisplayName(report: ReportPayload) {
  return report.tenantDisplayName || report.id_tenant || 'Empresa não identificada'
}

function formatProductName(typeServico?: string | null) {
  if (!typeServico) return ''
  return produtosLabels[typeServico as TypeServico] ?? typeServico
}

function formatProductStatus(status?: string | null) {
  if (!status) return ''
  return friendlyNameMaps.produtosStatus?.[status as keyof typeof friendlyNameMaps.produtosStatus] ?? status
}
